import BaseEntity from "./BaseEntity";

export default class AnswerType extends BaseEntity {
    answerTypeName: string;
    variation: string;
    isScored?: boolean;
    isFillInAnswer: boolean;
    excludeFromModuleScoreCalculation?: boolean;
}

export enum AnswerTypeNames {
    ScaledResponse = "Scaled Response",
    ScaledBooleanResponse = "Scaled Boolean Response",
    TagResponse = "Tag Response",
    ScoredTagResponse = "Scored Tag Response",
    StarRating = "Star Rating",
    QuestionGrouping = "Question Group",

    DateResponse = "Date Response",
    TextResponse = "Text Response",
}

export enum AnswerTypeVariations {
    Single = "Single",
}

/**
 * The answer types whose answers are a choice from a fixed set of tags, and so can be filtered or
 * grouped on. Free-text, date, and star-rating questions have no tag list to offer.
 *
 * Shared by the Question Result filter and Question Result grouping: a question that can be
 * filtered on must be groupable and vice versa, so the two read one definition rather than keeping
 * copies that can drift.
 */
export const MULTIPLE_CHOICE_ANSWER_TYPES: ReadonlySet<string> = new Set([
    AnswerTypeNames.TagResponse,
    AnswerTypeNames.ScaledResponse,
    AnswerTypeNames.ScoredTagResponse,
    AnswerTypeNames.ScaledBooleanResponse,
]);

/**
 * Whether a question can be filtered or grouped on: active, and answered by choosing from a fixed
 * tag list.
 *
 * Typed by shape rather than against `Question` so this module stays free of the model graph.
 */
export const isMultipleChoiceQuestion = (question: {
    isActive?: boolean;
    answerType?: { answerTypeName?: string };
}): boolean =>
    question.isActive !== false &&
    MULTIPLE_CHOICE_ANSWER_TYPES.has(question.answerType?.answerTypeName ?? "");

/**
 * Orders a question's answer options the way its module configures them, which is the order the
 * chart's bars are then sorted into.
 *
 * An option with no `order` sorts last rather than first, so a newly added one does not jump the
 * queue. Ties fall back to the option text, making the order total rather than dependent on the
 * array the API happened to return.
 */
export const compareAnswerOptionsByModuleOrder = (
    left: { order?: number | null; value?: string | null },
    right: { order?: number | null; value?: string | null },
): number => {
    const leftOrder =
        typeof left.order === "number" ? left.order : Number.MAX_SAFE_INTEGER;
    const rightOrder =
        typeof right.order === "number" ? right.order : Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) return leftOrder - rightOrder;

    return `${left.value ?? ""}`.localeCompare(`${right.value ?? ""}`, undefined, {
        sensitivity: "base",
    });
};
